import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { autoInsurancePage, companyInfo } from '../mock';
import { CheckCircle2, Phone, AlertCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const AutoInsurance = () => {
  const { language } = useLanguage();
  const content = autoInsurancePage[language];
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);

  useEffect(() => {
    // Check if script is already loaded
    if (document.querySelector('script[src="https://api.agencystream.com/widget/auto-quote-widget.js"]')) {
      setScriptLoaded(true);
      return;
    }

    // Load the AgencyStream widget script
    const script = document.createElement('script');
    script.src = 'https://api.agencystream.com/widget/auto-quote-widget.js';
    script.async = true;
    
    script.onload = () => {
      setScriptLoaded(true);
      setScriptError(false);
    };
    
    script.onerror = () => {
      setScriptError(true);
      setScriptLoaded(false);
    };
    
    document.body.appendChild(script);

    // Cleanup function
    return () => {
      // Note: We don't remove the script on cleanup to avoid reloading it
      // if the user navigates back to this page
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {content.title}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8">
              {content.subtitle}
            </p>
            <p className="text-lg text-blue-50">
              {content.description}
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            {content.howItWorks.title}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {content.howItWorks.steps.map((step) => (
              <Card key={step.step} className="text-center border-none shadow-lg">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Important Question - Rideshare/Delivery Usage */}
      <section className="py-12 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-l-4 border-blue-600 shadow-lg">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {language === 'en' 
                        ? 'Important: Commercial Vehicle Use Disclosure'
                        : 'Importante: Declaración de Uso Comercial del Vehículo'}
                    </h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {language === 'en'
                        ? 'If you use or plan to use your vehicle for rideshare, delivery, taxi, or paid passenger transport, you MUST inform us to ensure proper coverage. Please review and select all that apply before requesting your quote:'
                        : 'Si usa o planea usar su vehículo para rideshare, delivery, taxi o transporte remunerado de pasajeros, DEBE informarnos para asegurar la cobertura apropiada. Por favor revise y seleccione todo lo que aplique antes de solicitar su cotización:'}
                    </p>

                    <div className="bg-white p-4 rounded-lg space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                        <input type="checkbox" className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="text-gray-800">
                          {language === 'en' ? 'None - Personal use only' : 'Ninguno - Uso personal solamente'}
                        </span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                        <input type="checkbox" className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="text-gray-800">
                          {language === 'en' ? 'Rideshare (Uber, Lyft)' : 'Rideshare (Uber, Lyft)'}
                        </span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                        <input type="checkbox" className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="text-gray-800">
                          {language === 'en' ? 'Food/Package Delivery (Uber Eats, DoorDash, etc.)' : 'Entrega de Comida/Paquetes (Uber Eats, DoorDash, etc.)'}
                        </span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                        <input type="checkbox" className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="text-gray-800">
                          {language === 'en' ? 'Taxi service' : 'Servicio de taxi'}
                        </span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                        <input type="checkbox" className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" />
                        <span className="text-gray-800">
                          {language === 'en' ? 'Other paid passenger transport' : 'Otro transporte remunerado de pasajeros'}
                        </span>
                      </label>

                      <div className="pt-3 border-t border-gray-200">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {language === 'en' 
                            ? 'Please specify platforms or activity (if applicable):'
                            : 'Por favor especifique plataformas o actividad (si aplica):'}
                        </label>
                        <input
                          type="text"
                          placeholder={language === 'en' ? 'E.g., Amazon Flex, Instacart, etc.' : 'Ej: Amazon Flex, Instacart, etc.'}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mt-4 italic">
                      {language === 'en'
                        ? 'Note: This information will be discussed with your agent during the quote process to ensure proper coverage.'
                        : 'Nota: Esta información será discutida con su agente durante el proceso de cotización para asegurar la cobertura apropiada.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Widget Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-2xl border-none">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
                  {language === 'en' ? 'Get Your Free Quote Now' : 'Obtenga Su Cotización Gratis Ahora'}
                </h2>
                
                {/* AgencyStream Widget */}
                {scriptError ? (
                  <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
                    <div className="flex items-start">
                      <AlertCircle className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-red-800 font-semibold mb-2">
                          {content.widgetError}
                        </p>
                        <a 
                          href={`tel:${companyInfo.phone}`}
                          className="inline-flex items-center gap-2 text-red-700 font-bold hover:text-red-900 transition-colors"
                        >
                          <Phone className="w-5 h-5" />
                          {companyInfo.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                ) : !scriptLoaded ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
                    <p className="mt-4 text-gray-600">
                      {language === 'en' ? 'Loading quote form...' : 'Cargando formulario de cotización...'}
                    </p>
                  </div>
                ) : (
                  <div className="auto-insurance-widget">
                    <auto-quote-widget 
                      agency-id="94788" 
                      button-buy-now="false" 
                      carrier-name="false"
                    ></auto-quote-widget>
                  </div>
                )}

                {/* Help Section */}
                <div className="mt-8 pt-8 border-t border-gray-200 text-center">
                  <p className="text-gray-600 mb-3">
                    {content.needHelp}
                  </p>
                  <a 
                    href={`tel:${companyInfo.phone}`}
                    className="inline-flex items-center gap-2 text-blue-700 font-bold text-xl hover:text-blue-800 transition-colors"
                  >
                    <Phone className="w-6 h-6" />
                    {companyInfo.phone}
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Why Choose Us?' : '¿Por Qué Elegirnos?'}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {content.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AutoInsurance;
